'use client';

import { Button } from '@/components/ui/button';
import { useProductView } from '@/providers/product-view';
import { SvgCartArrow, SvgRight } from '@/components/icons-eataly';
import { Spinner } from '@/components/spinner';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';
import { getEventbriteExternalCta } from '@/lib/product-utils';
import { getNewTabLinkAttributes } from '@/components/link';
import { useTranslation } from 'react-i18next';
import { type ReactElement } from 'react';

/**
 * Purchase action for the event/course booking box.
 *
 * Unlike the standard {@link ProductCartActionsCustom}, events and courses have no
 * quantity picker in the booking box (per Figma). Free events link to Eventbrite;
 * paid events do the same in browse-only mode, otherwise they reuse the shared cart
 * state from {@link useProductView}.
 */
export default function EventCourseCartButton(): ReactElement | null {
    const { t } = useTranslation('product');
    const { product, handleAddToCart, canAddToCart, isAddingToOrUpdatingCart } = useProductView();
    const commerceEnabled = useCommerceEnabled();
    const eventbriteCta = getEventbriteExternalCta(product, { commerceEnabled });

    if (eventbriteCta) {
        if (!eventbriteCta.url) return null;

        // Product-specific aria-label already includes the new-tab phrase.
        const newTabAttrs = getNewTabLinkAttributes();

        return (
            <Button asChild hierarchy="primary" iconAfter={<SvgRight />} className="w-full" size="large">
                <a href={eventbriteCta.url} {...newTabAttrs} aria-label={t('eventCourse.eventbriteCtaAriaLabel')}>
                    {t('eventCourse.eventbriteCta')}
                </a>
            </Button>
        );
    }

    if (!commerceEnabled) {
        return null;
    }

    return (
        <Button
            data-testid="add-to-cart"
            onClick={() => void handleAddToCart()}
            disabled={!canAddToCart || isAddingToOrUpdatingCart}
            hierarchy="primary"
            iconAfter={isAddingToOrUpdatingCart ? undefined : <SvgCartArrow />}
            className="w-full"
            size="large">
            {isAddingToOrUpdatingCart ? <Spinner size="sm" /> : t('addToCart')}
        </Button>
    );
}
